import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PredictionForm = () => {
  const [schema, setSchema] = useState(null);
  const [features, setFeatures] = useState({});
  const [confidence, setConfidence] = useState(0.90);
  const [predictions, setPredictions] = useState(null);
  const [similarExperiments, setSimilarExperiments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [error, setError] = useState(null);

  // Load schema on mount
  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/schema`)
      .then(response => {
        setSchema(response.data);
        // Initialize features with empty values
        const initialFeatures = {};
        response.data.required_features.forEach(feature => {
          initialFeatures[feature] = '';
        });
        setFeatures(initialFeatures);
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to load schema. Is the API running?');
        setLoading(false);
      });
  }, []);

  const handleFeatureChange = (featureName, value) => {
    setFeatures(prev => ({
      ...prev,
      [featureName]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate: at least 2 additives must have a non-empty, non-zero value
    const additiveFeatures = schema.required_features.filter(f => f.startsWith('ADD_'));
    const filledAdditives = additiveFeatures.filter(f => {
      const val = features[f];
      return val !== '' && val !== null && val !== undefined && parseFloat(val) !== 0;
    });

    if (filledAdditives.length < 2) {
      setError('Please enter values for at least 2 additives before predicting.');
      return;
    }

    setLoading(true);

    try {
      // Convert numeric strings to numbers; default empty ADD_ fields to 0
      const processedFeatures = {};
      Object.keys(features).forEach(key => {
        const featureType = schema.feature_types[key];
        if (featureType === 'numeric') {
          if (key.startsWith('ADD_') && (features[key] === '' || features[key] === null || features[key] === undefined)) {
            processedFeatures[key] = 0;
          } else {
            processedFeatures[key] = parseFloat(features[key]);
          }
        } else {
          processedFeatures[key] = features[key];
        }
      });

      const response = await axios.post(`${API_URL}/predict`, {
        features: processedFeatures,
        confidence: confidence
      });

      setPredictions(response.data);

      // Fetch similar experiments
      setLoadingSimilar(true);
      try {
        const similarResponse = await axios.post(`${API_URL}/find-similar`, {
          features: processedFeatures,
          top_k: 10
        });
        setSimilarExperiments(similarResponse.data);
      } catch (simErr) {
        console.error('Failed to fetch similar experiments:', simErr);
        // Don't block main prediction on similarity error
      } finally {
        setLoadingSimilar(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Prediction failed. Please check your inputs.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !schema) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading schema...</p>
      </div>
    );
  }

  if (error && !schema) {
    return (
      <div className="error-container">
        <h3>Error</h3>
        <p>{error}</p>
        <p>Make sure the backend API is running at {API_URL}</p>
      </div>
    );
  }

  if (!schema) return null;

  const coreFeatures = schema.required_features.filter(f => !f.startsWith('ADD_'));
  const additiveFeatures = schema.required_features.filter(f => f.startsWith('ADD_'));

  const renderField = (featureName, isRequired) => {
    const featureType = schema.feature_types[featureName];
    return (
      <div key={featureName} className="form-group">
        <label htmlFor={featureName}>
          {featureName}
          <span className="feature-type-badge">{featureType}</span>
          {!isRequired && <span className="optional-badge">Optional</span>}
        </label>

        {featureType === 'categorical' ? (
          <select
            id={featureName}
            value={features[featureName]}
            onChange={(e) => handleFeatureChange(featureName, e.target.value)}
            required={isRequired}
          >
            <option value="">Select...</option>
            {schema.categorical_values[featureName]?.map(val => (
              <option key={val} value={val}>{val}</option>
            ))}
          </select>
        ) : (
          <input
            type="number"
            id={featureName}
            value={features[featureName]}
            onChange={(e) => handleFeatureChange(featureName, e.target.value)}
            step="any"
            placeholder={
              schema.numeric_ranges?.[featureName]
                ? `${schema.numeric_ranges[featureName].min.toFixed(1)} - ${schema.numeric_ranges[featureName].max.toFixed(1)}`
                : isRequired ? 'Enter value' : 'Enter value (default: 0)'
            }
            required={isRequired}
          />
        )}
      </div>
    );
  };

  return (
    <div className="prediction-container">
      <div className="intro-section">
        <h2>Forward Prediction</h2>
        <p>Enter cement formulation features to predict thickening times (TT40, TT70, TT100) with confidence intervals.</p>
      </div>

      <form onSubmit={handleSubmit} className="prediction-form">
        {/* Core Required Fields */}
        <div className="form-section">
          <h3>Required Fields</h3>
          <div className="features-grid">
            {coreFeatures.map(featureName => renderField(featureName, true))}
          </div>
        </div>

        {/* Additive Fields (Optional) */}
        <div className="form-section">
          <h3>
            Additives
            <span className="section-subtitle"> — at least 2 required, others default to 0</span>
          </h3>
          <div className="features-grid">
            {additiveFeatures.map(featureName => renderField(featureName, false))}
          </div>
        </div>

        <div className="form-section">
          <h3>Prediction Settings</h3>
          <div className="form-group confidence-slider">
            <label htmlFor="confidence">
              Confidence Level: <strong>{(confidence * 100).toFixed(0)}%</strong>
            </label>
            <input
              type="range"
              id="confidence"
              min="0.5"
              max="0.99"
              step="0.01"
              value={confidence}
              onChange={(e) => setConfidence(parseFloat(e.target.value))}
            />
            <div className="range-labels">
              <span>50%</span>
              <span>99%</span>
            </div>
          </div>
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Predicting...' : 'Predict Thickening Times'}
        </button>
      </form>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {predictions && (
        <div className="results-section">
          <h2>Prediction Results</h2>

          <div className="sequential-flow-info">
            <h4>Sequential Model Flow:</h4>
            <div className="flow-steps">
              {Object.entries(predictions.sequential_flow).map(([step, flow]) => (
                <div key={step} className="flow-step">{flow}</div>
              ))}
            </div>
          </div>

          <div className="predictions-grid">
            {(() => {
              const displayOrder = ['TT40_min', 'TT70_min', 'TT100_min'];
              const entries = Object.entries(predictions.predictions);
              const sorted = displayOrder
                .filter(key => predictions.predictions[key] !== undefined)
                .map(key => [key, predictions.predictions[key]]);
              // Append any unexpected keys not in displayOrder
              entries.forEach(([k, v]) => {
                if (!displayOrder.includes(k)) sorted.push([k, v]);
              });
              return sorted.map(([target, pred]) => (
                <div key={target} className="prediction-card">
                  <h3>{target.replace('_', ' ')}</h3>
                  <div className="prediction-main">
                    <div className="prediction-value">
                      {pred.prediction.toFixed(2)}
                      <span className="unit">minutes</span>
                    </div>
                  </div>
                  <div className="confidence-interval">
                    <div className="ci-label">
                      {(confidence * 100).toFixed(0)}% Confidence Interval
                    </div>
                    <div className="ci-range">
                      [{pred.lower_ci.toFixed(2)}, {pred.upper_ci.toFixed(2)}]
                    </div>
                    <div className="ci-width">
                      Width: {pred.ci_width.toFixed(2)} min
                    </div>
                  </div>
                  <div className="prediction-bar">
                    <div
                      className="prediction-marker"
                      style={{ left: '50%' }}
                      title={`Prediction: ${pred.prediction.toFixed(2)}`}
                    ></div>
                    <div className="ci-bar"></div>
                  </div>
                </div>
              ));
            })()}
          </div>

          <div className="metadata-section">
            <h4>Model Information</h4>
            <div className="metadata-grid">
              <div><strong>Model Type:</strong> {predictions.metadata.model_type}</div>
              <div><strong>Base Estimator:</strong> {predictions.metadata.base_estimator}</div>
              <div><strong>Confidence Level:</strong> {(predictions.confidence_level * 100).toFixed(0)}%</div>
            </div>
          </div>
        </div>
      )}

      {/* Similar Experiments Section */}
      {predictions && (
        <div className="similar-experiments-section">
          <h2>Similar Experiments from Database</h2>
          <p className="section-description">
            Top 10 most similar experiments based on Euclidean distance in feature space
          </p>

          {loadingSimilar && (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Finding similar experiments...</p>
            </div>
          )}

          {similarExperiments && !loadingSimilar && (
            <>
              <div className="similar-info">
                <span className="badge">{similarExperiments.similar_experiments.length} experiments found</span>
                <span className="badge">Using {similarExperiments.features_used_for_similarity.length} features for similarity</span>
              </div>

              <div className="similar-table-container">
                <table className="similar-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Similarity</th>
                      <th>TT40</th>
                      <th>TT70</th>
                      <th>TT100</th>
                      <th>Location</th>
                      <th>Density</th>
                      <th>BHCT</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {similarExperiments.similar_experiments.map((item, idx) => {
                      const exp = item.experiment;
                      const similarity = Math.max(0, 100 - item.distance * 10); // Convert distance to similarity %

                      return (
                        <tr key={idx}>
                          <td className="rank-cell">{idx + 1}</td>
                          <td className="similarity-cell">
                            <div className="similarity-bar-container">
                              <div
                                className="similarity-bar"
                                style={{ width: `${Math.min(100, similarity)}%` }}
                              ></div>
                              <span className="similarity-text">{similarity.toFixed(1)}%</span>
                            </div>
                          </td>
                          <td>{exp['TT40_min'] ? exp['TT40_min'].toFixed(1) : '-'}</td>
                          <td>{exp['TT70_min'] ? exp['TT70_min'].toFixed(1) : '-'}</td>
                          <td>{exp['TT100_min'] ? exp['TT100_min'].toFixed(1) : '-'}</td>
                          <td>{exp['Location'] || '-'}</td>
                          <td>{exp['Density'] ? exp['Density'].toFixed(1) : '-'}</td>
                          <td>{exp['BHCT'] ? exp['BHCT'].toFixed(1) : '-'}</td>
                          <td>
                            <button
                              className="details-button"
                              onClick={() => {
                                alert(JSON.stringify(exp, null, 2));
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PredictionForm;
